// app/frontend/pages/Auth/Login.tsx
import { useState } from "react";
import { useForm } from "@mantine/form";
import { Stack, Anchor, Group } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { L2KH1, L2KH2, L2KText, L2KCaption } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import {
  L2KInput,
  L2KPasswordInput,
  L2KCheckbox,
  L2KButton,
} from "../../components/ui/Forms";
import AuthFormLayout from "../../components/layouts/AuthFormLayout";
import { SEO } from "../../components/common/SEO";

interface Props {
  return_to?: string;
}

export default function Login({ return_to }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      remember_me: false,
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      password: (value) => (value.length < 1 ? "Password is required" : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const formData = new FormData();
      formData.append("user[email]", values.email);
      formData.append("user[password]", values.password);
      formData.append("user[remember_me]", values.remember_me ? "1" : "0");

      const response = await fetch("/login", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Welcome back!",
          message: "You have been logged in successfully.",
          icon: <IconCheck />,
          autoClose: 3000,
        });

        setTimeout(() => {
          window.location.href = return_to || "/";
        }, 500);
      } else {
        const data = await response.json().catch(() => ({}));
        notifications.show({
          color: "red",
          title: "Login Failed",
          message: data.error || "Invalid email or password",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
        form.setErrors({ password: "Invalid email or password" });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred during login",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthFormLayout>
      <SEO title="Sign In - Learntokiz" />
      <Stack gap={isMobile ? "md" : "lg"}>
        <Stack gap={isMobile ? 4 : "xs"} ta="center">
          {isMobile ? (
            <L2KH2 variant="accent">Welcome back!</L2KH2>
          ) : (
            <L2KH1 variant="accent">Welcome back!</L2KH1>
          )}
          {isMobile ? (
            <L2KCaption variant="secondary">
              New accounts open at 2.0 launch.
            </L2KCaption>
          ) : (
            <L2KText variant="secondary">
              New accounts open at 2.0 launch.
            </L2KText>
          )}
        </Stack>

        <L2KCard variant="elevated" glow p={isMobile ? "md" : "xl"}>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap={isMobile ? "sm" : "md"}>
              <L2KInput
                label="Email"
                placeholder="you@example.com"
                required
                size={isMobile ? "sm" : "md"}
                {...form.getInputProps("email")}
              />
              <L2KPasswordInput
                label="Password"
                placeholder="Your password"
                required
                size={isMobile ? "sm" : "md"}
                {...form.getInputProps("password")}
              />
              <Group justify="space-between" align="center" wrap="nowrap">
                <L2KCheckbox
                  label="Remember me"
                  size={isMobile ? "xs" : "sm"}
                  {...form.getInputProps("remember_me", { type: "checkbox" })}
                />
                <Anchor
                  href="/password/new"
                  size={isMobile ? "xs" : "sm"}
                  style={{ color: "#FF9800", whiteSpace: "nowrap" }}
                >
                  Forgot password?
                </Anchor>
              </Group>
              <L2KButton
                fullWidth
                mt={isMobile ? "xs" : "md"}
                size={isMobile ? "sm" : "md"}
                type="submit"
                loading={isLoading}
              >
                Sign In
              </L2KButton>
            </Stack>
          </form>
        </L2KCard>
      </Stack>
    </AuthFormLayout>
  );
}
